import { Injectable } from '@angular/core';
import { map, catchError, of, tap, firstValueFrom } from 'rxjs';

import { HttpHelperService } from '@shared/services/http-helper.service';
import { HealthcheckResponse } from '../types/healthcheck';
import {
  FeedbackRequest,
  FeedbackResponse,
  Session,
  Preferences,
  Subset,
  BaseSidekickRequest,
  AvailableModel,
  AvailableModelsResponse,
  MissingInfoRequest,
  GetDocQueryParams,
  AttachmentSource,
} from '../types/sidekick-api';
import { ProductDocuments } from '../types/documents';
import { ProductContext } from '../types/productContext';
import { SessionService } from './session.service';
import { ProductService } from './product.service';
import { EventService } from './event.service';
import { AuthService } from '@shared/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AIService {
  constructor(
    private readonly http: HttpHelperService,
    private readonly sessionService: SessionService,
    private readonly productService: ProductService,
    private readonly eventService: EventService,
    private readonly authService: AuthService,
  ) {}

  async askSidekick(input: string, sessionId = '', requestId = '', productContext = '', generationModelArn = ''): Promise<any | undefined> {
    const start = new Date();

    const request = this.createSidekickRequest(input, sessionId, requestId, productContext, generationModelArn);

    this.eventService.publishEvent({
      eventName: 'output_generation',
      requestId,
    });

    return firstValueFrom(
      this.http.post('/answers', request).pipe(
        map((response) => {
          const end = new Date();
          this.productService.setActiveProduct(response.context?.product);
          this.productService.setActiveTopic(response.context?.topic);
          return {
            ...response,
            response_time: (end.getTime() - start.getTime()) / 1000,
          };
        }),
      ),
    );
  }

  async searchSidekick(input: string, sessionId = '', requestId = '', productContext = ''): Promise<any | undefined> {
    const start = new Date();

    const request = this.createSidekickRequest(input, sessionId, requestId, productContext);

    this.eventService.publishEvent({
      eventName: 'output_search',
      requestId,
    });

    return firstValueFrom(
      this.http.post('/search', request).pipe(
        map((response) => {
          const end = new Date();
          this.productService.setActiveProduct(response.context?.product);
          this.productService.setActiveTopic(response.context?.topic);
          return {
            ...response,
            response_time: (end.getTime() - start.getTime()) / 1000,
          };
        }),
      ),
    );
  }

  async getEnabledProducts(): Promise<ProductContext[]> {
    return firstValueFrom(
      this.http.get('/products').pipe(
        map((response) => {
          this.productService.updateEnabledProducts(response.product_list as ProductContext[]);
          this.productService.updateEnabledTopics(
            Array.from(
              new Set(response.product_list.map((product: ProductContext) => product.topic).filter((topic: string | null) => topic !== null)),
            ),
          );
          return response.product_list as ProductContext[];
        }),
      ),
    );
  }

  async getDocument(documentKey: string, params?: GetDocQueryParams): Promise<any> {
    const sessionId = this.sessionService.retrieveCurrentSessionId();
    console.log('requesting document>>>', documentKey);
    const headers = sessionId ? { 'Session-Id': sessionId } : {};

    const queryParams = params
      ? Object.entries(params)
          .filter(([_, value]) => !!value)
          .map(([key, value]) => `${key}=${value}`)
          .join('&')
      : '';

    this.eventService.publishEvent({
      eventName: params?.event_type,
      sourceName: params?.click_source,
      requestId: params?.request_id,
      metaData: { ...params },
    });

    return firstValueFrom(
      this.http
        .get(`/documents/${encodeURIComponent(documentKey)}${queryParams ? `?${queryParams}` : ''}`, '', {
          responseType: 'arraybuffer',
          headers: headers,
        })
        .pipe(
          map((response) => {
            console.log('Document retrieval successful');
            const file = new Blob([response], { type: 'application/pdf' });
            return URL.createObjectURL(file);
          }),
          catchError((err: any) => {
            console.log('Document retrieval failed', err);
            this.http.handleErrorResponse('Document retrieval failed');
            return of({ err: 'Document retrieval failed' });
          }),
        ),
    );
  }

  async getSources(): Promise<ProductDocuments[]> {
    return firstValueFrom(
      this.http.get('/sources').pipe(
        map((response) => {
          // Filter out any product sources that have a value of "true" for "is_hidden"
          // These are files we create that are not sourced from Urbo or other NF repos
          for (const product of response.source_list) {
            product.sources = product.sources.filter((source: AttachmentSource) => !source.is_hidden);
          }
          return response.source_list as ProductDocuments[];
        }),
      ),
    );
  }

  async healthCheck(): Promise<HealthcheckResponse | Object> {
    try {
      return await firstValueFrom(this.http.get<HealthcheckResponse | Object>(`/healthchecks`));
    } catch (err) {
      console.log('Error in #healthcheck:', err);
      throw err;
    }
  }

  async submitFeedback(feedbackRequest: FeedbackRequest): Promise<FeedbackResponse> {
    this.eventService.publishEvent({
      eventName: 'add_feedback',
      requestId: feedbackRequest.request_id,
      metaData: { ...feedbackRequest },
    });

    return firstValueFrom(
      this.http.post('/feedback', feedbackRequest).pipe(
        map((response) => {
          return response as FeedbackResponse;
        }),
      ),
    );
  }

  async getPreferences(): Promise<Preferences> {
    return firstValueFrom(
      this.http.get<Preferences>('/settings').pipe(
        map((response: Preferences) => {
          response.success = undefined; // idk if I like this, but we are on a tight deadline, so if anyone has good suggestions, lets add it later
          return response;
        }),
      ),
    );
  }

  async updatePreferences(preferences: Subset<Preferences>): Promise<Preferences> {
    return firstValueFrom(
      this.http.patch('/settings', preferences).pipe(
        map((response) => {
          return response;
        }),
      ),
    );
  }

  async createSession(): Promise<Session> {
    return firstValueFrom(
      this.http.post('/sessions', { createSession: true }).pipe(
        tap((response) => {
          this.sessionService.updateSessionId(response.session_id);
          this.sessionService.updateSessionUserAccess(response.access_level);
          this.eventService.publishEvent({
            eventName: 'create_session',
            sessionId: response.session_id,
            requestId: response.request_id,
          });
        }),
      ),
    );
  }

  async getAvailableModels(): Promise<AvailableModel[]> {
    return firstValueFrom(
      this.http.get('/models').pipe(
        map((response: AvailableModelsResponse) => {
          return response.available_models;
        }),
      ),
    );
  }

  async requestMissingInfo(missingInfoRequest: MissingInfoRequest): Promise<unknown> {
    const accessToken = await firstValueFrom(this.authService.azureV2AccessToken$);

    return firstValueFrom(
      this.http
        .post('/missing-info-requests', {
          ...missingInfoRequest,
          accessToken,
          session_id: this.sessionService.retrieveCurrentSessionId(),
        })
        .pipe(
          map((response) => {
            return response;
          }),
        ),
    );
  }

  private readonly createSidekickRequest = (
    input: string,
    sessionId?: string,
    requestId?: string,
    productContext?: string,
    generationModelArn?: string,
  ) => {
    let request: BaseSidekickRequest = {
      input,
    };
    if (productContext) {
      request = {
        ...request,
        context: {
          product: productContext,
        },
      };
    }
    if (sessionId) {
      request = {
        ...request,
        session_id: sessionId,
      };
    }
    if (requestId) {
      request = {
        ...request,
        request_id: requestId,
      };
    }
    if (generationModelArn) {
      request = {
        ...request,
        options: {
          generation_model_arn: generationModelArn,
        },
      };
    }
    return request;
  };
}
